import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSmile, faPaperPlane, faImage, faPaperclip, faEllipsisV, faSpinner, faCheck, faCheckDouble, faClock } from '@fortawesome/free-solid-svg-icons';
import '../../assets/styles/Chat.css';

// 聊天窗口顶部标题栏
const ChatHeader = ({ session }) => {
  if (!session) return null;
  
  return (
    <div className="chat-header">
      <div className="chat-header-info">
        <img src={session.displayAvatar} alt={session.displayName} className="chat-header-avatar" />
        <span className="chat-header-name">{session.displayName}</span>
      </div>
      <div className="chat-header-actions">
        <button className="chat-header-button">
          <FontAwesomeIcon icon={faEllipsisV} />
        </button>
      </div>
    </div>
  );
};

// 消息气泡组件
const MessageBubble = ({ message }) => {
  // 安全获取当前登录用户信息
  const [userInfo, setUserInfo] = useState(null);
  
  useEffect(() => {
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        const parsedInfo = JSON.parse(userInfoStr);
        // 标准化用户信息字段
        const standardizedInfo = {
          id: parsedInfo.id || 0,
          nickname: parsedInfo.nickname || parsedInfo.nickName || parsedInfo.userAccount || '用户',
          avatar: parsedInfo.avatar || parsedInfo.avatarUrl || 'https://via.placeholder.com/36'
        };
        setUserInfo(standardizedInfo);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  }, []);
  
  // 判断消息是否是当前用户发送的
  const isCurrentUserMessage = userInfo && (
    Number(message.senderId) === Number(userInfo.id) || 
    (message.clientMessageId && !message.senderId) // 临时消息（尚未收到服务器确认）
  );
  
  // 消息状态图标
  const getStatusIcon = () => {
    if (message.pending) {
      return <FontAwesomeIcon icon={faClock} className="message-status-icon" />;
    } else {
      // 这里可以根据实际需求添加已送达、已读等状态
      return <FontAwesomeIcon icon={faCheck} className="message-status-icon" />;
    }
  };

  // 格式化时间
  const formatTime = (timeString) => {
    try {
      if (!timeString) return '';
      return new Date(timeString).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      console.error('格式化时间失败:', error);
      return '';
    }
  };
  
  return (
    <div className={`message-container ${isCurrentUserMessage ? 'mine' : 'other'}`}>
      {!isCurrentUserMessage && (
        <div className="message-avatar">
          <img 
            src={message.senderAvatar || 'https://via.placeholder.com/36'} 
            alt={message.senderNickname || '用户'} 
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/36';
            }}
          />
        </div>
      )}
      <div className="message-content-wrapper">
        {!isCurrentUserMessage && (
          <div className="message-sender">{message.senderNickname || '用户'}</div>
        )}
        <div className={`message-bubble ${isCurrentUserMessage ? 'mine' : 'other'}`}>
          {message.messageType === 1 && (
            <div className="message-text">{message.content || ''}</div>
          )}
          {/* 可以根据需要添加其他消息类型的渲染 */}
        </div>
        <div className="message-info">
          <span className="message-time">
            {formatTime(message.createdAt)}
          </span>
          {isCurrentUserMessage && getStatusIcon()}
        </div>
      </div>
      {isCurrentUserMessage && (
        <div className="message-avatar">
          <img 
            src={userInfo?.avatar || 'https://via.placeholder.com/36'} 
            alt={userInfo?.nickname || '我'} 
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/36';
            }}
          />
        </div>
      )}
    </div>
  );
};

// 消息区域组件
const MessageArea = ({ messages, loadMoreMessages, hasMore, isLoading }) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);
  
  // 处理手动点击加载更多
  const handleLoadMore = () => {
    if (hasMore && !isLoading && !isLoadingMore) {
      setIsLoadingMore(true);
      
      // 记录当前滚动位置和内容高度
      const container = messagesContainerRef.current;
      const scrollHeight = container ? container.scrollHeight : 0;
      const scrollTop = container ? container.scrollTop : 0;
      
      // 加载更多消息
      loadMoreMessages();
      
      // 在消息加载后保持滚动位置
      setTimeout(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          // 保持相对滚动位置
          container.scrollTop = scrollTop + (newScrollHeight - scrollHeight);
        }
        setIsLoadingMore(false);
      }, 500); // 增加延迟时间，确保DOM已完全更新
    }
  };
  
  // 处理滚动加载更多历史消息
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container && container.scrollTop === 0 && hasMore && !isLoading && !isLoadingMore) {
      setIsLoadingMore(true);
      
      // 记录当前滚动位置和内容高度
      const scrollHeight = container.scrollHeight;
      
      // 加载更多消息
      loadMoreMessages();
      
      // 在消息加载后保持滚动位置
      setTimeout(() => {
        const newScrollHeight = container.scrollHeight;
        if (container && newScrollHeight > scrollHeight) {
          // 计算新内容的高度，并将滚动位置设置为新内容高度
          const heightDiff = newScrollHeight - scrollHeight;
          container.scrollTop = heightDiff;
        }
        setIsLoadingMore(false);
      }, 500); // 增加延迟时间，确保DOM已完全更新
    }
  };
  
  // 强制滚动到底部的函数
  const forceScrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [scrollToBottom]);
  
  // 首次加载消息时，强制滚动到底部
  useEffect(() => {
    if (messages.length > 0 && !isLoading && !initialScrollDone) {
      forceScrollToBottom();
      setInitialScrollDone(true);
    }
  }, [messages.length, isLoading, forceScrollToBottom, initialScrollDone]);
  
  // 会话切换时，重置状态并滚动到底部
  useEffect(() => {
    setInitialScrollDone(false);
    if (messages.length > 0) {
      forceScrollToBottom();
    }
  }, [messages[0]?.sessionId, forceScrollToBottom, messages.length]);
  
  // 新消息到达时，如果用户在底部，则自动滚动到底部
  useEffect(() => {
    // 只有当不是加载更多历史消息时才考虑自动滚动
    if (initialScrollDone && messages.length > 0 && !isLoading && !isLoadingMore) {
      const container = messagesContainerRef.current;
      if (container) {
        // 检查用户是否在底部附近（距离底部不超过100px）
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom) {
          scrollToBottom();
        }
      }
    }
  }, [messages, isLoading, initialScrollDone, isLoadingMore, scrollToBottom]);
  
  return (
    <div 
      className="message-area" 
      ref={messagesContainerRef}
      onScroll={handleScroll}
      style={{ 
        backgroundColor: '#f5f5f5',
        backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\' viewBox=\'0 0 100 100\'><path fill=\'%23e8e8e8\' d=\'M20,20 L80,20 L80,80 L20,80 Z M30,30 L70,30 L70,70 L30,70 Z\'/></svg>")',
        backgroundRepeat: 'repeat',
        backgroundSize: '50px 50px'
      }}
    >
      {hasMore && (
        <div className="load-more">
          {isLoading || isLoadingMore ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <span onClick={handleLoadMore}>加载更多</span>
          )}
        </div>
      )}
      
      <div className="messages-container">
        {messages.map((message) => (
          <MessageBubble 
            key={message.messageId || message.clientMessageId} 
            message={message}
          />
        ))}
      </div>
      
      <div ref={messagesEndRef} className="messages-end-marker" />
    </div>
  );
};

// 消息输入区域组件
const MessageInput = ({ onSendMessage }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);
  
  // 自动调整文本框高度
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message]);
  
  // 发送消息
  const handleSendMessage = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };
  
  // 处理按键事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  return (
    <div className="message-input">
      <div className="message-input-actions">
        <button className="message-input-button">
          <FontAwesomeIcon icon={faSmile} />
        </button>
        <button className="message-input-button">
          <FontAwesomeIcon icon={faImage} />
        </button>
        <button className="message-input-button">
          <FontAwesomeIcon icon={faPaperclip} />
        </button>
      </div>
      <div className="message-input-content">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="输入消息..."
          rows="1"
        />
      </div>
      <button 
        className={`message-send-button ${!message.trim() ? 'disabled' : ''}`}
        onClick={handleSendMessage}
        disabled={!message.trim()}
      >
        <FontAwesomeIcon icon={faPaperPlane} />
      </button>
    </div>
  );
};

// 主聊天窗口组件
const ChatWindow = () => {
  const { currentSession, messages, sendMessage, loadMoreMessages, hasMore, isLoading } = useChat();
  
  // 如果没有选中的会话，显示默认界面
  if (!currentSession) {
    return (
      <div className="chat-window empty">
        <div className="chat-window-placeholder">
          <p>选择一个会话开始聊天</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="chat-window">
      <ChatHeader session={currentSession} />
      <MessageArea 
        messages={messages} 
        loadMoreMessages={loadMoreMessages}
        hasMore={hasMore}
        isLoading={isLoading}
      />
      <MessageInput onSendMessage={sendMessage} />
    </div>
  );
};

export default ChatWindow; 